import type { RadarConfig } from '@/types/radar';

export interface FilterState {
  quadrant?: string;
  ring?: string;
  tags: string[];
  search: string;
  changeStatus?: string;
}

/**
 * Filter panel component for controlling radar visibility
 */
export class FilterPanel {
  private container: HTMLElement;
  private config: RadarConfig;
  private state: FilterState;
  private onChange?: (state: FilterState) => void;
  private allTags: Set<string>;

  constructor(container: HTMLElement, config: RadarConfig, allTags: string[]) {
    this.container = container;
    this.config = config;
    this.allTags = new Set(allTags);
    this.state = {
      tags: [],
      search: '',
    };

    this.render();
  }

  /**
   * Set change handler
   */
  setChangeHandler(handler: (state: FilterState) => void): void {
    this.onChange = handler;
  }

  /**
   * Get current filter state
   */
  getState(): FilterState {
    return { ...this.state };
  }

  /**
   * Set filter state programmatically
   */
  setState(state: Partial<FilterState>): void {
    this.state = {
      ...this.state,
      ...state,
    };
    this.render();
  }

  /**
   * Reset all filters
   */
  reset(): void {
    this.state = {
      tags: [],
      search: '',
    };
    this.render();
    this.notifyChange();
  }

  /**
   * Render the filter panel
   */
  private render(): void {
    this.container.innerHTML = `
      <div class="filter-panel">
        <div class="filter-row">
          <div class="filter-group">
            <label class="filter-label">Search</label>
            <input
              type="text"
              class="filter-search"
              placeholder="Search technologies..."
              value="${this.state.search}"
            />
          </div>

          <div class="filter-group">
            <label class="filter-label">Quadrant</label>
            <select class="filter-quadrant">
              <option value="">All Quadrants</option>
              ${this.config.quadrants
                .map(
                  (q) =>
                    `<option value="${q.id}" ${this.state.quadrant === q.id ? 'selected' : ''}>${q.name}</option>`
                )
                .join('')}
            </select>
          </div>

          <div class="filter-group">
            <label class="filter-label">Ring</label>
            <select class="filter-ring">
              <option value="">All Rings</option>
              ${this.config.rings
                .map(
                  (r) =>
                    `<option value="${r.id}" ${this.state.ring === r.id ? 'selected' : ''}>${r.name}</option>`
                )
                .join('')}
            </select>
          </div>

          <div class="filter-group">
            <label class="filter-label">Change Status</label>
            <select class="filter-change">
              <option value="">All</option>
              <option value="new" ${this.state.changeStatus === 'new' ? 'selected' : ''}>New</option>
              <option value="moved" ${this.state.changeStatus === 'moved' ? 'selected' : ''}>Moved</option>
              <option value="unchanged" ${this.state.changeStatus === 'unchanged' ? 'selected' : ''}>Unchanged</option>
            </select>
          </div>

          ${this.allTags.size > 0 ? `
            <div class="filter-group">
              <label class="filter-label">Tags</label>
              <div class="tags-dropdown-container">
                <input
                  type="text"
                  class="tags-search"
                  placeholder="Search and select tags..."
                  autocomplete="off"
                />
                <div class="tags-dropdown-list" style="display: none;">
                  ${Array.from(this.allTags)
                    .sort()
                    .map(
                      (tag) => `
                      <label class="tags-dropdown-item">
                        <input
                          type="checkbox"
                          value="${tag}"
                          ${this.state.tags.includes(tag) ? 'checked' : ''}
                        />
                        <span>${tag}</span>
                      </label>`
                    )
                    .join('')}
                </div>
                ${this.state.tags.length > 0 ? `
                  <div class="selected-tags">
                    ${this.state.tags.map(tag => `
                      <span class="tag-pill" data-tag="${tag}">
                        ${tag}
                        <button class="tag-remove" data-tag="${tag}">&times;</button>
                      </span>
                    `).join('')}
                  </div>
                ` : ''}
              </div>
            </div>
          ` : ''}

          <div class="filter-group">
            <button class="btn btn-reset">Reset</button>
          </div>
        </div>
      </div>
    `;

    this.attachEventListeners();
  }

  /**
   * Attach event listeners
   */
  private attachEventListeners(): void {
    const searchInput = this.container.querySelector('.filter-search') as HTMLInputElement;
    const quadrantSelect = this.container.querySelector('.filter-quadrant') as HTMLSelectElement;
    const ringSelect = this.container.querySelector('.filter-ring') as HTMLSelectElement;
    const changeSelect = this.container.querySelector('.filter-change') as HTMLSelectElement;
    const tagsSearchInput = this.container.querySelector('.tags-search') as HTMLInputElement;
    const tagsDropdownList = this.container.querySelector('.tags-dropdown-list') as HTMLElement;
    const tagCheckboxes = this.container.querySelectorAll('.tags-dropdown-item input');
    const resetBtn = this.container.querySelector('.btn-reset');

    // Search input with debounce
    let searchTimeout: number;
    searchInput?.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = window.setTimeout(() => {
        this.state.search = (e.target as HTMLInputElement).value;
        this.notifyChange();
      }, 300);
    });

    quadrantSelect?.addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value;
      this.state.quadrant = value || undefined;
      this.notifyChange();
    });

    ringSelect?.addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value;
      this.state.ring = value || undefined;
      this.notifyChange();
    });

    changeSelect?.addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value;
      this.state.changeStatus = value || undefined;
      this.notifyChange();
    });

    // Tags searchable dropdown
    tagsSearchInput?.addEventListener('focus', () => {
      if (tagsDropdownList) {
        tagsDropdownList.style.display = 'block';
      }
    });

    tagsSearchInput?.addEventListener('input', (e) => {
      const searchValue = (e.target as HTMLInputElement).value.toLowerCase();
      const items = this.container.querySelectorAll('.tags-dropdown-item');

      items.forEach((item) => {
        const text = item.textContent?.toLowerCase() || '';
        (item as HTMLElement).style.display = text.includes(searchValue) ? 'flex' : 'none';
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (tagsDropdownList && !this.container.contains(e.target as Node)) {
        tagsDropdownList.style.display = 'none';
      }
    });

    // Tag checkbox changes
    tagCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        const checkedTags = Array.from(tagCheckboxes)
          .filter((cb) => (cb as HTMLInputElement).checked)
          .map((cb) => (cb as HTMLInputElement).value);
        this.state.tags = checkedTags;
        this.render();
        this.notifyChange();
      });
    });

    // Tag removal
    const tagRemoveBtns = this.container.querySelectorAll('.tag-remove');
    tagRemoveBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const tag = (btn as HTMLElement).getAttribute('data-tag');
        if (tag) {
          this.state.tags = this.state.tags.filter(t => t !== tag);
          this.render();
          this.notifyChange();
        }
      });
    });

    resetBtn?.addEventListener('click', () => this.reset());
  }

  /**
   * Notify listeners of state change
   */
  private notifyChange(): void {
    if (this.onChange) {
      this.onChange(this.state);
    }
  }
}
